/**
 * PrintPanel — Print and export tools (wired to map canvas via PrintService)
 */
import React, { useCallback } from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Print, Image, Map } from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  openPrintDialog,
  openImageExportDialog,
  openLayoutComposer,
  setExporting,
  setExportError,
  closeImageExportDialog,
} from '../../../store/slices/printExportSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import ImageExportDialog from '../../ImageExportDialog';
import LayoutComposer from '../../LayoutComposer';
import { mapNavigator } from '../../../services/MapNavigator';
import { exportMapImage, downloadBlob } from '../../../services/PrintService';

const PrintPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const imageOptions = useAppSelector((s) => s.printExport.imageExportOptions);

  const handleImageExport = useCallback(() => {
    const map = mapNavigator.getMap();
    if (!map) {
      dispatch(setExportError('Map is not ready yet.'));
      return;
    }
    try {
      dispatch(setExporting(true));
      const blob = exportMapImage(map, imageOptions);
      const ext = imageOptions.format || 'png';
      downloadBlob(blob, `geovera-map.${ext}`);
      dispatch(setExporting(false));
      dispatch(closeImageExportDialog());
      dispatch(addNotification({ type: 'success', message: `Image exported (.${ext})` }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed';
      dispatch(setExportError(msg));
      dispatch(setExporting(false));
    }
  }, [dispatch, imageOptions]);

  const tools = [
    {
      label: 'Print to PDF',
      desc: 'Generate a PDF with title, legend, scale bar',
      icon: <Print />,
      action: () => dispatch(openPrintDialog()),
    },
    {
      label: 'Export Image',
      desc: 'Save map as PNG, JPG, or SVG',
      icon: <Image />,
      action: () => dispatch(openImageExportDialog()),
    },
    {
      label: 'Layout Composer',
      desc: 'Design a custom map layout',
      icon: <Map />,
      action: () => dispatch(openLayoutComposer()),
    },
  ];

  return (
    <Box>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: palette.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontWeight: 600,
          }}
        >
          Output
        </Typography>
      </Box>
      <List dense disablePadding>
        {tools.map((tool) => (
          <ListItemButton
            key={tool.label}
            onClick={tool.action}
            sx={{ px: 1.5, py: 0.75, mx: 0.5, borderRadius: tokens.radius.sm }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: palette.accent.primary }}>
              {React.cloneElement(tool.icon as React.ReactElement, { sx: { fontSize: 20 } })}
            </ListItemIcon>
            <ListItemText
              primary={tool.label}
              secondary={tool.desc}
              primaryTypographyProps={{ sx: { fontSize: tokens.fontSize.md, fontWeight: 500 } }}
              secondaryTypographyProps={{ sx: { fontSize: tokens.fontSize.xs } }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* PrintDialog is mounted once in WebGISWorkspace so Simple mode can use it too */}
      <ImageExportDialog onExport={handleImageExport} />
      <LayoutComposer />
    </Box>
  );
};

export default PrintPanel;
